import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from './use-toast';
import { useValidarConformidade } from './useConformidade';

export interface EmpPlanilhaVersao {
  id: string;
  contrato_id: string;
  numero_versao: number;
  descricao: string | null;
  motivo_revisao: string | null;
  status: string;
  data_vigencia: string | null;
  valor_total: number;
  total_itens: number;
  total_grupos: number;
  importacao_id: string | null;
  aditivo_id: string | null;
  dirty_financeiro: boolean;
  valor_consolidado: number;
  created_at: string;
  updated_at: string;
  created_by: string;
}

export interface EmpPlanilhaGrupo {
  id: string;
  versao_id: string;
  grupo_pai_id: string | null;
  codigo: string;
  nome: string;
  nivel: number;
  ordem: number;
}

export type MetodoMedicaoItem = 'ppu' | 'eap' | 'ubs' | 'os_ppu' | 'os_eap';

export interface EmpPlanilhaItem {
  id: string;
  versao_id: string;
  grupo_id: string | null;
  codigo_item: string;
  descricao: string;
  unidade: string;
  quantidade_contratada: number;
  preco_unitario: number;
  valor_total: number | null;
  valor_total_fixo: number | null;
  peso_financeiro: number | null;
  peso_fisico: number | null;
  status: string;
  origem: string;
  ordem: number;
  metodo_medicao: string | null;
}

export function useEmpPlanilhaVersoes(contratoId: string) {
  return useQuery({
    queryKey: ['emp-planilha-versoes', contratoId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('emp_planilha_versoes')
        .select('*')
        .eq('contrato_id', contratoId)
        .order('numero_versao', { ascending: false });
      if (error) throw error;
      return data as EmpPlanilhaVersao[];
    },
    enabled: !!contratoId,
  });
}

export function useEmpPlanilhaDetalhes(versaoId: string | null) {
  const gruposQuery = useQuery({
    queryKey: ['emp-planilha-grupos', versaoId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('emp_planilha_grupos')
        .select('*')
        .eq('versao_id', versaoId!)
        .order('ordem', { ascending: true });
      if (error) throw error;
      return data as EmpPlanilhaGrupo[];
    },
    enabled: !!versaoId,
  });

  const itensQuery = useQuery({
    queryKey: ['emp-planilha-itens', versaoId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('emp_planilha_itens')
        .select('*')
        .eq('versao_id', versaoId!)
        .order('ordem', { ascending: true });
      if (error) throw error;
      return data as EmpPlanilhaItem[];
    },
    enabled: !!versaoId,
  });

  return {
    grupos: gruposQuery.data || [],
    itens: itensQuery.data || [],
    isLoading: gruposQuery.isLoading || itensQuery.isLoading,
  };
}

export function useEmpPlanilhaImport() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const validarConformidade = useValidarConformidade();

  return useMutation({
    mutationFn: async (payload: {
      contrato_id: string;
      descricao?: string;
      motivo_revisao?: string;
      grupos: Array<{ codigo: string; nome: string; nivel: number; grupo_pai_codigo?: string; ordem?: number }>;
      itens: Array<{
        codigo_item: string; descricao: string; unidade: string;
        quantidade_contratada: number; preco_unitario: number;
        grupo_codigo?: string; peso_fisico?: number | null; ordem?: number;
        valor_total?: number | null;
      }>;
      ativar_imediatamente?: boolean;
      preservar_pesos?: boolean;
    }) => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Não autenticado');

      const resp = await supabase.functions.invoke('importar-planilha', {
        body: payload,
      });

      if (resp.error) throw new Error(resp.error.message || 'Erro na importação');
      if (!resp.data?.success) throw new Error('Importação com erros: ' + JSON.stringify(resp.data?.erros));
      return { ...resp.data, contrato_id: payload.contrato_id };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['emp-planilha-versoes'] });
      toast({ title: `Planilha v${data.numero_versao} importada`, description: `${data.registros_sucesso} itens, ${data.grupos_criados} grupos` });

      // Auto-trigger cross-validation
      validarConformidade.mutate({
        contrato_id: data.contrato_id,
        trigger_type: 'auto_planilha',
      });
    },
    onError: (err: Error) => {
      toast({ title: 'Erro na importação', description: err.message, variant: 'destructive' });
    },
  });
}

export function useEmpPlanilhaAtivar() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (versaoId: string) => {
      const { error } = await supabase
        .from('emp_planilha_versoes')
        .update({ status: 'vigente', data_vigencia: new Date().toISOString(), updated_at: new Date().toISOString() })
        .eq('id', versaoId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['emp-planilha-versoes'] });
      queryClient.invalidateQueries({ queryKey: ['emp-planilha-itens'] });
      queryClient.invalidateQueries({ queryKey: ['emp-contratos'] });
      queryClient.invalidateQueries({ queryKey: ['emp-contrato'] });
      queryClient.invalidateQueries({ queryKey: ['emp-planilha-items-select'] });
      queryClient.invalidateQueries({ queryKey: ['emp-linter'] });
      toast({ title: 'Versão ativada com sucesso', description: 'Pesos físicos e financeiros calculados automaticamente.' });
    },
    onError: (err: Error) => {
      toast({ title: 'Erro ao ativar versão', description: err.message, variant: 'destructive' });
    },
  });
}

export function useEmpPlanilhaItemUpdate() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ itemId, metodo_medicao }: { itemId: string; metodo_medicao: string }) => {
      const { error } = await supabase
        .from('emp_planilha_itens')
        .update({ metodo_medicao } as any)
        .eq('id', itemId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['emp-planilha-itens'] });
      queryClient.invalidateQueries({ queryKey: ['emp-contrato-metodos'] });
      toast({ title: 'Método de medição atualizado' });
    },
    onError: (err: Error) => {
      toast({ title: 'Erro ao atualizar método', description: err.message, variant: 'destructive' });
    },
  });
}

/** Hook to query distinct metodo_medicao values for a contract's vigente planilha */
export function useEmpContratoMetodos(contratoId: string) {
  return useQuery({
    queryKey: ['emp-contrato-metodos', contratoId],
    queryFn: async () => {
      // Get vigente version
      const { data: versoes } = await supabase
        .from('emp_planilha_versoes')
        .select('id')
        .eq('contrato_id', contratoId)
        .eq('status', 'vigente')
        .limit(1);
      const versaoId = versoes?.[0]?.id;
      if (!versaoId) return [] as string[];

      const { data: itens } = await supabase
        .from('emp_planilha_itens')
        .select('metodo_medicao')
        .eq('versao_id', versaoId)
        .not('metodo_medicao', 'is', null);

      const methods = new Set((itens || []).map((i: any) => i.metodo_medicao).filter(Boolean));
      return Array.from(methods);
    },
    enabled: !!contratoId,
  });
}
